import { memo, useCallback, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import rehypeSanitize from 'rehype-sanitize';
import type { Components } from 'react-markdown';

interface MarkdownViewProps {
  markdownText: string;
  /** data-* id for TOC scroll target lookup */
  contentId?: string;
}

/**
 * #652 — 상대 `.md` href 판정.
 * `#` 앵커 · 절대 `http(s)://` · 절대 경로(`/`로 시작) 는 제외하고,
 * `.md` 로 끝나거나 `.md#섹션` 형태인 상대 링크만 true.
 * 결합 문서 내 `../data-source-matrix.md` · `./ADR-001-*.md` 류가
 * SPA 라우터로 떨어져 404 되는 것을 막기 위해 비링크 렌더 대상으로 판정.
 */
export function isRelativeMdHref(href: string | undefined | null): boolean {
  if (!href) return false;
  if (href.startsWith('#')) return false;
  if (/^https?:\/\//i.test(href)) return false;
  if (href.startsWith('/')) return false;
  return /\.md(#.*)?$/i.test(href);
}

/** mermaid 코드블록 — lazy 렌더 with fallback to <pre> */
function MermaidBlock({ code }: { code: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const rendered = useRef(false);

  useEffect(() => {
    if (rendered.current) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      async ([entry]) => {
        if (!entry.isIntersecting || rendered.current) return;
        rendered.current = true;
        observer.disconnect();
        try {
          const mermaid = await import('mermaid').then((m) => m.default);
          mermaid.initialize({ startOnLoad: false, theme: 'default' });
          const id = `mermaid-${Math.random().toString(36).slice(2)}`;
          const { svg } = await mermaid.render(id, code);
          el.innerHTML = svg;
        } catch {
          // fallback — keep the <pre> displayed by setting a flag
          el.dataset['fallback'] = 'true';
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [code]);

  return (
    <div ref={ref} data-mermaid="true" style={{ margin: 'var(--spacing-4) 0' }}>
      {/* Initial fallback — overwritten by mermaid on intersection */}
      <pre
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-4)',
          fontSize: 'var(--font-size-xs)',
          fontFamily: 'var(--font-family-mono)',
          overflowX: 'auto',
          color: 'var(--color-text-secondary)',
        }}
      >
        <code>{code}</code>
      </pre>
    </div>
  );
}

const components: Components = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  code({ className, children, ...props }: any) {
    const lang = /language-(\w+)/.exec(className ?? '')?.[1];
    const content = String(children).replace(/\n$/, '');

    if (lang === 'mermaid') {
      return <MermaidBlock code={content} />;
    }

    // inline code
    if (!className) {
      return (
        <code
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: '0.875em',
            background: 'var(--color-surface-muted)',
            padding: '0.1em 0.35em',
            borderRadius: 'var(--radius-sm)',
            color: 'var(--color-brand-primary)',
          }}
          {...props}
        >
          {children}
        </code>
      );
    }

    return (
      <pre
        style={{
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          padding: 'var(--spacing-4)',
          overflowX: 'auto',
          margin: 'var(--spacing-4) 0',
        }}
      >
        <code
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-primary)',
          }}
          className={className}
          {...props}
        >
          {children}
        </code>
      </pre>
    );
  },

  // 테이블 스타일
  table({ children }) {
    return (
      <div style={{ overflowX: 'auto', margin: 'var(--spacing-4) 0' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          {children}
        </table>
      </div>
    );
  },
  th({ children }) {
    return (
      <th
        style={{
          padding: 'var(--spacing-2) var(--spacing-3)',
          borderBottom: '2px solid var(--color-surface-border)',
          textAlign: 'left',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          background: 'var(--color-surface-muted)',
          fontSize: 'var(--font-size-xs)',
        }}
      >
        {children}
      </th>
    );
  },
  td({ children }) {
    return (
      <td
        style={{
          padding: 'var(--spacing-2) var(--spacing-3)',
          borderBottom: '1px solid var(--color-surface-border)',
          color: 'var(--color-text-primary)',
        }}
      >
        {children}
      </td>
    );
  },

  // blockquote
  blockquote({ children }) {
    return (
      <blockquote
        style={{
          borderLeft: '4px solid var(--color-brand-primary)',
          margin: 'var(--spacing-4) 0',
          paddingLeft: 'var(--spacing-4)',
          color: 'var(--color-text-secondary)',
          fontStyle: 'italic',
        }}
      >
        {children}
      </blockquote>
    );
  },

  // 헤딩
  h1({ children, ...props }) {
    return (
      <h1
        style={{
          fontSize: 'var(--font-size-2xl)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          margin: 'var(--spacing-8) 0 var(--spacing-4)',
          borderBottom: '2px solid var(--color-surface-border)',
          paddingBottom: 'var(--spacing-2)',
        }}
        {...props}
      >
        {children}
      </h1>
    );
  },
  h2({ children, ...props }) {
    return (
      <h2
        style={{
          fontSize: 'var(--font-size-xl)',
          fontWeight: 600,
          color: 'var(--color-text-primary)',
          margin: 'var(--spacing-6) 0 var(--spacing-3)',
        }}
        {...props}
      >
        {children}
      </h2>
    );
  },
  h3({ children, ...props }) {
    return (
      <h3
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 600,
          color: 'var(--color-text-primary)',
          margin: 'var(--spacing-4) 0 var(--spacing-2)',
        }}
        {...props}
      >
        {children}
      </h3>
    );
  },

  // 링크
  a({ children, href, ...props }) {
    // #652 — 상대 .md 링크는 SPA 라우터가 삼켜 404 로 이탈하므로 비링크 텍스트로 렌더
    if (isRelativeMdHref(href)) {
      return (
        <span
          title={href ?? undefined}
          aria-label={href ?? undefined}
          style={{
            fontFamily: 'var(--font-family-mono)',
            fontSize: '0.9em',
            color: 'var(--color-text-secondary)',
            borderBottom: '1px dashed var(--color-surface-border)',
            cursor: 'default',
          }}
        >
          {children}
        </span>
      );
    }

    return (
      <a
        href={href}
        style={{ color: 'var(--color-brand-primary)' }}
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
        {...props}
      >
        {children}
      </a>
    );
  },

  // 수평선
  hr() {
    return (
      <hr
        style={{
          border: 'none',
          borderTop: '1px solid var(--color-surface-border)',
          margin: 'var(--spacing-6) 0',
        }}
      />
    );
  },
};

export const MarkdownView = memo(function MarkdownView({
  markdownText,
  contentId,
}: MarkdownViewProps) {
  // rehype-sanitize default schema 는 id/class 허용하지 않아 TOC anchor 가 사라짐 — 커스텀 스키마 사용
  const sanitizeSchema = {
    strip: ['script', 'style'],
    allowDangerousHtml: false,
    tagNames: undefined as unknown as string[], // allow all safe tags
    attributes: {
      '*': ['id', 'class', 'aria-hidden', 'href', 'src', 'alt', 'title', 'type'],
      a: ['href', 'title', 'target', 'rel'],
      img: ['src', 'alt', 'title', 'width', 'height'],
    },
  };

  const noop = useCallback(() => undefined, []);
  void noop;

  return (
    <div
      data-standards-content="true"
      id={contentId}
      style={{
        lineHeight: 1.7,
        fontSize: 'var(--font-size-base)',
        color: 'var(--color-text-primary)',
        fontFamily: 'var(--font-family-sans)',
        minWidth: 0,
        flex: 1,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[
          rehypeSlug,
          [rehypeAutolinkHeadings, { behavior: 'append' }],
          [rehypeSanitize, sanitizeSchema],
        ]}
        components={components}
      >
        {markdownText}
      </ReactMarkdown>
    </div>
  );
});
